/**
 * Holds the checkout capability between the verification link and the return
 * from Stripe (#3017).
 *
 * The capability is a bearer secret (docs/operations/self-service-signup-
 * contract.md §3.5): memory plus `sessionStorage` only — Stripe sends the
 * browser back to the same tab — never a URL, never `localStorage`, never a
 * log line. When `sessionStorage` is unavailable (blocked storage) the
 * in-memory copy still carries a flow that does not reload.
 */

export const SIGNUP_SESSION_STORAGE_KEY = 'os-buddy:signup-checkout-session';

export interface SignupCheckoutSession {
  readonly id: string;
  readonly checkoutToken: string;
}

let memory: SignupCheckoutSession | null = null;

function isSession(value: unknown): value is SignupCheckoutSession {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    record.id.length > 0 &&
    typeof record.checkoutToken === 'string' &&
    record.checkoutToken.length > 0
  );
}

export function saveSignupSession(session: SignupCheckoutSession): void {
  memory = { id: session.id, checkoutToken: session.checkoutToken };
  try {
    sessionStorage.setItem(SIGNUP_SESSION_STORAGE_KEY, JSON.stringify(memory));
  } catch {
    // Storage blocked: the in-memory copy is what we have.
  }
}

export function loadSignupSession(): SignupCheckoutSession | null {
  if (memory) return memory;
  try {
    const raw = sessionStorage.getItem(SIGNUP_SESSION_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isSession(parsed)) {
      memory = { id: parsed.id, checkoutToken: parsed.checkoutToken };
      return memory;
    }
    sessionStorage.removeItem(SIGNUP_SESSION_STORAGE_KEY);
  } catch {
    // Unreadable or blocked storage reads as "no session".
  }
  return null;
}

export function clearSignupSession(): void {
  memory = null;
  try {
    sessionStorage.removeItem(SIGNUP_SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is blocked.
  }
}
